import { useEffect, useMemo, useState } from 'react'
import { useFormState, useWatch } from 'react-hook-form'
import type { Control } from 'react-hook-form'

import { computePaymentFormula } from '#/entities/shipment'
import type { PaymentFormulaResult } from '#/entities/shipment'
import { useFormWithZod } from '#/shared/lib/form'
import { EMPTY_PAYMENT_DEFAULTS, usePaymentSchema } from './payment-schema'
import type { PaymentFormData } from './payment-schema'

function useWatchedFields(control: Control<PaymentFormData>) {
  const watched = useWatch({ control }) as Partial<PaymentFormData>
  return {
    totalTariff: watched.totalTariff ?? '',
    deposit: watched.deposit ?? '',
    carrierFee: watched.carrierFee ?? '',
    selectValues: {
      paymentMethod: watched.paymentMethod ?? '',
      paymentTerms: watched.paymentTerms ?? '',
      balanceMethod: watched.balanceMethod ?? '',
      balanceTime: watched.balanceTime ?? '',
      balanceTermBegins: watched.balanceTermBegins ?? '',
    },
  }
}

interface UsePaymentFormParams {
  defaultValues?: Partial<PaymentFormData>
  onSubmit?: (data: PaymentFormData) => void | Promise<void>
}

function parseMoney(raw: string | undefined): number {
  if (!raw) return 0
  const n = Number.parseFloat(raw.replace(/,/g, ''))
  return Number.isFinite(n) ? n : 0
}

function formatComputedMoney(amount: number): string {
  return amount % 1 === 0 ? String(amount) : amount.toFixed(2)
}

export function usePaymentForm({
  defaultValues,
  onSubmit,
}: UsePaymentFormParams = {}) {
  const schema = usePaymentSchema()
  const values = useMemo<PaymentFormData>(
    () => ({ ...EMPTY_PAYMENT_DEFAULTS, ...defaultValues }),
    [defaultValues],
  )
  const form = useFormWithZod(schema, {
    defaultValues: values,
    values,
    resetOptions: { keepDirtyValues: true },
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const [carrierFeeManual, setCarrierFeeManual] = useState<boolean>(() =>
    Boolean(defaultValues?.carrierFee),
  )

  const { totalTariff, deposit, carrierFee, selectValues } = useWatchedFields(
    form.control,
  )
  const { errors, dirtyFields } = useFormState({ control: form.control })
  const isDirty = Object.keys(dirtyFields).length > 0

  useEffect(() => {
    if (carrierFeeManual) return
    const hasInputs = totalTariff !== '' || deposit !== ''
    if (!hasInputs) {
      if (form.getValues('carrierFee') !== '') {
        form.setValue('carrierFee', '', {
          shouldDirty: false,
          shouldValidate: false,
        })
      }
      return
    }
    const fee = Math.max(0, parseMoney(totalTariff) - parseMoney(deposit))
    const fmt = formatComputedMoney(fee)
    if (form.getValues('carrierFee') === fmt) return
    form.setValue('carrierFee', fmt, {
      shouldDirty: false,
      shouldValidate: false,
    })
  }, [totalTariff, deposit, carrierFeeManual, form])

  const formula: PaymentFormulaResult = useMemo(
    () =>
      computePaymentFormula({
        totalTariff,
        deposit,
        carrierPay: carrierFee,
      }),
    [totalTariff, deposit, carrierFee],
  )

  const onCarrierFeeChange = (value: string) => {
    setCarrierFeeManual(value.length > 0)
  }

  return {
    control: form.control,
    register: form.register,
    getValues: form.getValues,
    setValue: form.setValue,
    trigger: form.trigger,
    errors,
    isSubmitting: form.isSubmitting,
    isDirty,
    handleSubmit: onSubmit ? form.handleSubmit(onSubmit) : undefined,
    reset: form.reset,
    onCarrierFeeChange,
    formula,
    selectValues,
  }
}
